import type { JSX } from 'react';

import { Spinner, Text } from '@change/ds-core-ui';
import { CheckmarkVerifiedIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import { useScrollToSection, useSignFormStep } from '#src/app/pages/petitionDetails/shared/hooks';
import {
	SIGN_FORM_STEP_HEADING_FOCUS_TARGET,
	signFormStepFocusFallback,
} from '#src/app/pages/petitionDetails/shared/utilities';
import { useFocusFallbackRef } from '#src/app/shared/hooks/focus';

import { SignFormContainer } from '../signForm';

import { useEmbedSignFormExperiment } from './hooks';

export function EmbeddedSignForm(): JSX.Element | null {
	const {
		data: { isEnabled },
	} = useEmbedSignFormExperiment();

	const {
		data: { refs },
	} = useScrollToSection();

	const { signFormStep } = useSignFormStep();

	const focusFallbackRef = useFocusFallbackRef(signFormStepFocusFallback);

	if (!isEnabled) {
		return null;
	}

	return (
		<div id={SECTION_ID_MAPPING.EMBEDDED_SIGN_FORM} ref={refs[SECTION_ID_MAPPING.EMBEDDED_SIGN_FORM]}>
			{/* using programmatic media query to allow lazy loading of the sign form in modal on mobile */}
			{/* Spinner is necessary for SSR */}
			<ClientRender placeholder={<Spinner className="mx-auto mt-16 block" />}>
				<div ref={focusFallbackRef} className="contents">
					{signFormStep === 1 && (
						<Text
							className="mt-5 mb-4"
							variant="heading"
							as="h2"
							size="x-small"
							data-focus-target={SIGN_FORM_STEP_HEADING_FOCUS_TARGET}
						>
							<Translate value="fe.components.petition_sign.complete_signature" />
						</Text>
					)}
					{signFormStep === 2 && (
						<div className="mt-5 mb-4 flex flex-col gap-4">
							<div className="flex items-center gap-2.5">
								<CheckmarkVerifiedIcon
									className="text-icon-information"
									data-testid="embedded-form-header-checkmark-icon"
								/>
								<div>
									<Text
										variant="heading"
										as="h2"
										size="x-small"
										data-focus-target={SIGN_FORM_STEP_HEADING_FOCUS_TARGET}
									>
										<Translate value="corgi.petition_gamma.petition_sign.address.verify_title" />
									</Text>
								</div>
							</div>
							<div>
								<Text variant="body" size="small">
									<Translate value="corgi.petition_gamma.petition_sign.address.disclaimer" />
								</Text>
							</div>
						</div>
					)}

					<div>
						<SignFormContainer dataQa="sign-form-mobile" />
					</div>
				</div>
			</ClientRender>
		</div>
	);
}
